import { RouteObject } from 'react-router-dom';
import { lazy } from 'react';

const HomePage = lazy(() => import('../pages/home/page'));
const CategoryPage = lazy(() => import('../pages/category/page'));
const ArticlePage = lazy(() => import('../pages/article/page'));
const NotFound = lazy(() => import('../pages/NotFound'));
const AboutPage = lazy(() => import('../pages/about/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const PrivacyPage = lazy(() => import('../pages/privacy/page'));
const DisclaimerPage = lazy(() => import('../pages/disclaimer/page'));
const AffiliateDisclosurePage = lazy(() => import('../pages/affiliate-disclosure/page'));
const Blog = lazy(() => import('../pages/blog/page'));
const BlogPost = lazy(() => import('../pages/blog-post/page'));
const KetoBakingForBeginners = lazy(() => import('../pages/blog-post/keto-baking-for-beginners'));
const KetoBakingBeginnersGuide = lazy(() => import('../pages/blog-post/keto-baking-beginners-guide'));
const CommonKetoBakingMistakes = lazy(() => import('../pages/blog-post/common-keto-baking-mistakes'));
const WhyKetoDessertArePopular = lazy(() => import('../pages/blog-post/why-keto-desserts-are-popular'));
const EssentialToolsForKetoBaking = lazy(() => import('../pages/blog-post/essential-tools-for-keto-baking'));
const MitolynIngredientsReview = lazy(() => import('../pages/blog-post/mitolyn-ingredients-review'));
const MitolynIngredientsReviewPost = lazy(() => import('../pages/blog-post/mitolyn-ingredients-review'));
const MitolynScamOrLegitPost = lazy(() => import('../pages/blog-post/mitolyn-scam-or-legit'));
const MitolynSideEffectsPost = lazy(() => import('../pages/blog-post/mitolyn-side-effects'));
const WhereToBuyMitolynPost = lazy(() => import('../pages/blog-post/where-to-buy-mitolyn'));
const MitolynPriceBreakdown = lazy(() => import('../pages/blog-post/mitolyn-price-breakdown'));
const HowToTakeMitolyn = lazy(() => import('../pages/blog-post/how-to-take-mitolyn'));
const DoesMitolynReallyWork = lazy(() => import('../pages/blog-post/does-mitolyn-really-work'));
const WhySlowMetabolismMakesWeightLossHard = lazy(() => import('../pages/blog-post/why-slow-metabolism-makes-weight-loss-hard'));

// Review Pages
const PianoforallReview = lazy(() => import('../pages/reviews/pianoforall-review/page'));
const LibertyTokenReview = lazy(() => import('../pages/reviews/liberty-token-review/page'));
const LaserlessTattooRemovalReview = lazy(() => import('../pages/reviews/laserless-tattoo-removal-review/page'));

// Betting Strategy Reviews
const TheLayFormulaReview = lazy(() => import('../pages/reviews/the-lay-formula-review/page'));
const SevenBets4FreeReview = lazy(() => import('../pages/reviews/7-bets-4-free-review/page'));
const DailyFootballWinnersReview = lazy(() => import('../pages/reviews/daily-football-winners-review/page'));
const ZcodeSystemReview = lazy(() => import('../pages/reviews/zcode-system-review/page'));
const ProfitMaximiserReview = lazy(() => import('../pages/reviews/profit-maximiser-review/page'));
const BettingGodsReview = lazy(() => import('../pages/reviews/betting-gods-review/page'));
const FootballBoosterTipsReview = lazy(() => import('../pages/reviews/football-booster-tips-review/page'));
const BreakthroughBetsReview = lazy(() => import('../pages/reviews/breakthrough-bets-review/page'));
const BankOnFootballReview = lazy(() => import('../pages/reviews/bank-on-football-review/page'));
const LotteryMaximizerReview = lazy(() => import('../pages/reviews/lottery-maximizer-review/page'));
const ExterminatorSystemReview = lazy(() => import('../pages/reviews/exterminator-system-review/page'));
const BettingScientistReview = lazy(() => import('../pages/reviews/betting-scientist-review/page'));
const GoalPredictorReview = lazy(() => import('../pages/reviews/goal-predictor-review/page'));
const ValueMultiplierReview = lazy(() => import('../pages/reviews/value-multiplier-review/page'));

// Business & Wealth Reviews
const VipIndicatorsReview = lazy(() => import('../pages/reviews/vip-indicators-review/page'));
const WoodProfitsReview = lazy(() => import('../pages/reviews/woodprofits-review/page'));
const LeskoHelpReview = lazy(() => import('../pages/reviews/leskohelp-review/page'));
const SpySniperTradingSystemReview = lazy(() => import('../pages/reviews/spy-sniper-trading-system-review/page'));
const BusinessCreditBuilderReview = lazy(() => import('../pages/reviews/business-credit-builder-review/page'));
const OptionsPopReview = lazy(() => import('../pages/reviews/options-pop-review/page'));
const BakeADogABoneReview = lazy(() => import('../pages/reviews/bake-a-dog-a-bone-review/page'));
const MicroBankingMethodReview = lazy(() => import('../pages/reviews/microbanking-method-review/page'));

// Cooking & Kitchen Reviews
const FatBurningKitchenReview = lazy(() => import('../pages/reviews/fat-burning-kitchen-review/page'));
const CompletePlantBasedCookbookReview = lazy(() => import('../pages/reviews/complete-plant-based-cookbook-review/page'));
const KetoBakeryBundleReview = lazy(() => import('../pages/reviews/keto-bakery-bundle-review/page'));
const SmoothieRecipesReview = lazy(() => import('../pages/reviews/200-delicious-smoothie-recipes-review/page'));
const DessertRecipesReview = lazy(() => import('../pages/reviews/500-mouthwatering-dessert-recipes-review/page'));
const KetoRecipesReview = lazy(() => import('../pages/reviews/500-keto-diet-recipes-review/page'));
const VeganRecipesReview = lazy(() => import('../pages/reviews/250-vegan-recipes-review/page'));

// Digital Business & Marketing Reviews
const WriteAppReviewsReview = lazy(() => import('../pages/reviews/write-app-reviews-review/page'));
const ProfitVaultReview = lazy(() => import('../pages/reviews/profit-vault-review/page'));
const AIMarketersClubReview = lazy(() => import('../pages/reviews/ai-marketers-club-review/page'));
const EvolutionReview = lazy(() => import('../pages/reviews/evolution-review/page'));
const TenMinuteCashSystemReview = lazy(() => import('../pages/reviews/10-minute-cash-system-review/page'));
const EmpireReview = lazy(() => import('../pages/reviews/empire-review/page'));
const PaidOnlineWritingJobsReview = lazy(() => import('../pages/reviews/paid-online-writing-jobs-review/page'));
const SaleHooReview = lazy(() => import('../pages/reviews/salehoo-review/page'));
const ProfitFundamentalsReview = lazy(() => import('../pages/reviews/profit-fundamentals-review/page'));
const ClickEarnersReview = lazy(() => import('../pages/reviews/click-earners-review/page'));
const RecessionProfitSecretsReview = lazy(() => import('../pages/reviews/recession-profit-secrets-review/page'));
const IDPLRReview = lazy(() => import('../pages/reviews/idplr-review/page'));
const GoDigitalIncomeReview = lazy(() => import('../pages/reviews/go-digital-income-review/page'));
const EZCoverMakerReview = lazy(() => import('../pages/reviews/ez-cover-maker-review/page'));
const CommissionableReview = lazy(() => import('../pages/reviews/commissionable-review/page'));

const GetPaidSocialMediaReview = lazy(() => import('../pages/reviews/get-paid-social-media-review/page'));
const SecretsOfTheBigDogsReview = lazy(() => import('../pages/reviews/secrets-of-the-big-dogs-review/page'));
const LiveChatJobsReview = lazy(() => import('../pages/reviews/live-chat-jobs-review/page'));

const SocialMediaSecretsReview = lazy(() => import('../pages/reviews/2026-social-media-secrets-review/page'));
const InstaDoodleReview = lazy(() => import('../pages/reviews/instadoodle-review/page'));
const SpeecheloReview = lazy(() => import('../pages/reviews/speechelo-review/page'));
const VidelyReview = lazy(() => import('../pages/reviews/videly-review/page'));

// Careers & Work Life Reviews
const MedicalCourierJobsReview = lazy(() => import('../pages/reviews/medical-courier-jobs-review/page'));

// Eco Living & Sustainability Reviews
const EnergyRevolutionSystemReview = lazy(() => import('../pages/reviews/energy-revolution-system-review/page'));
const SeptifixReview = lazy(() => import('../pages/reviews/septifix-review/page'));
const UltimateOffGridGeneratorReview = lazy(() => import('../pages/reviews/ultimate-off-grid-generator-review/page'));
const EZBatteryReconditioningReview = lazy(() => import('../pages/reviews/ez-battery-reconditioning-review/page'));
const HomePowerShieldReview = lazy(() => import('../pages/reviews/home-power-shield-review/page'));
const KineticPowerSystemReview = lazy(() => import('../pages/reviews/kinetic-power-system-review/page'));
const SolarInnovatorReview = lazy(() => import('../pages/reviews/solar-innovator-review/page'));
const HendershotPowerGeneratorReview = lazy(() => import('../pages/reviews/hendershot-power-generator-review/page'));
const EasyDIYPowerPlanReview = lazy(() => import('../pages/reviews/easy-diy-power-plan-review/page'));
const QuickPowerSystemReview = lazy(() => import('../pages/reviews/quick-power-system-review/page'));
const InfiniteEnergySystemReview = lazy(() => import('../pages/reviews/infinite-energy-system-review/page'));
const TheUltimateEnergizerReview = lazy(() => import('../pages/reviews/the-ultimate-energizer-review/page'));
const HomeGridFreedomReview = lazy(() => import('../pages/reviews/home-grid-freedom-review/page'));
const TyrannyLiberatorReview = lazy(() => import('../pages/reviews/tyranny-liberator-review/page'));
const EnergyLiberationArmyReview = lazy(() => import('../pages/reviews/energy-liberation-army-review/page'));
const TornadoGeneratorReview = lazy(() => import('../pages/reviews/tornado-generator-review/page'));
const StirlingFreePowerReview = lazy(() => import('../pages/reviews/stirling-free-power-review/page'));
const GroundPowerGeneratorReview = lazy(() => import('../pages/reviews/ground-power-generator-review/page'));
const DIYDishSystemReview = lazy(() => import('../pages/reviews/diy-dish-system-review/page'));
const TheFreedomBoxReview = lazy(() => import('../pages/reviews/the-freedom-box-review/page'));

// Health, Fitness & Wellness Reviews
const SpartamaxReview = lazy(() => import('../pages/reviews/spartamax-review/page'));
const TheBloodPressureProgramReview = lazy(() => import('../pages/reviews/the-blood-pressure-program-review/page'));
const MitolynReview = lazy(() => import('../pages/reviews/mitolyn-review/page'));
const ProstaViveReview = lazy(() => import('../pages/reviews/prostavive-review/page'));
const CitrusBurnReview = lazy(() => import('../pages/reviews/citrusburn-review/page'));
const MitolynReviewPage = lazy(() => import("../pages/reviews/mitolyn-review/page"));
const ProstaviveReviewPage = lazy(() => import("../pages/reviews/prostavive-review/page"));
const CitrusburnReviewPage = lazy(() => import("../pages/reviews/citrusburn-review/page"));
const AudiofortReviewPage = lazy(() => import("../pages/reviews/audifort-review/page"));
const ProdentimReviewPage = lazy(() => import("../pages/reviews/prodentim-review/page"));
const NeuroEnergizerReviewPage = lazy(() => import("../pages/reviews/neuro-energizer-review/page"));
const Gluco6ReviewPage = lazy(() => import("../pages/reviews/gluco6-review/page"));
const ProvaDentReviewPage = lazy(() => import("../pages/reviews/provadent-review/page"));
const TheMemoryWaveReviewPage = lazy(() => import("../pages/reviews/the-memory-wave-review/page"));
const LeanBiomeReview = lazy(() => import('../pages/reviews/leanbiome-review/page'));
const TheSmoothieDietReview = lazy(() => import('../pages/reviews/the-smoothie-diet-review/page'));
const SleepLeanReview = lazy(() => import('../pages/reviews/sleeplean-review/page'));
const JavaBurnReview = lazy(() => import('../pages/reviews/java-burn-review/page'));
const IGenicsReview = lazy(() => import('../pages/reviews/igenics-review/page'));
const SugarDefenderReview = lazy(() => import('../pages/reviews/sugar-defender-review/page'));
const NeuroSergeReview = lazy(() => import('../pages/reviews/neuro-serge-review/page'));
const CardioSlimTeaReview = lazy(() => import('../pages/reviews/cardio-slim-tea-review/page'));
const FinessaReview = lazy(() => import('../pages/reviews/finessa-review/page'));
const PinealGuardianXReview = lazy(() => import('../pages/reviews/pineal-guardian-x-review/page'));
const LivPureReview = lazy(() => import('../pages/reviews/liv-pure-review/page'));
const IkariaJuiceReview = lazy(() => import('../pages/reviews/ikaria-juice-review/page'));
const NeuroBalanceTherapyReview = lazy(() => import('../pages/reviews/neuro-balance-therapy-review/page'));
const ArcticBlastReview = lazy(() => import('../pages/reviews/arcticblast-review/page'));
const HepatoBurnReview = lazy(() => import('../pages/reviews/hepatoburn-review/page'));
const ProstaPeakReview = lazy(() => import('../pages/reviews/prosta-peak-review/page'));
const GlucoExtendReview = lazy(() => import('../pages/reviews/gluco-extend-review/page'));
const BellyFlushReview = lazy(() => import('../pages/reviews/bellyflush-review/page'));
const BoneDensitySolutionReview = lazy(() => import('../pages/reviews/bone-density-solution-review/page'));
const ProtoflowReview = lazy(() => import('../pages/reviews/protoflow-review/page'));
const BioDentexReview = lazy(() => import('../pages/reviews/biodentex-review/page'));
const ZenCortexReview = lazy(() => import('../pages/reviews/zencortex-review/page'));
const NeuroPrimeReview = lazy(() => import('../pages/reviews/neuroprime-review/page'));
const NaganoTonicReview = lazy(() => import('../pages/reviews/nagano-tonic-review/page'));
const JointViveReview = lazy(() => import('../pages/reviews/jointvive-review/page'));
const GlucoBerryReview = lazy(() => import('../pages/reviews/glucoberry-review/page'));
const TitanFlowReview = lazy(() => import('../pages/reviews/titanflow-review/page'));
const MetaboDropsReview = lazy(() => import('../pages/reviews/metabo-drops-review/page'));
const TheParkinsonsProtocolReview = lazy(() => import('../pages/reviews/the-parkinsons-protocol-review/page'));
const SlimCrystalReview = lazy(() => import('../pages/reviews/slim-crystal-review/page'));
const FourteenDayRapidSoupDietReview = lazy(() => import('../pages/reviews/14-day-rapid-soup-diet-review/page'));
const SugarMuteReview = lazy(() => import('../pages/reviews/sugarmute-review/page'));
const ThyrafemmeBalanceReview = lazy(() => import('../pages/reviews/thyrafemme-balance-review/page'));
const GutVitaReview = lazy(() => import('../pages/reviews/gut-vita-review/page'));
const VenoPlus8Review = lazy(() => import('../pages/reviews/venoplus-8-review/page'));
const TheEdisonWaveReview = lazy(() => import('../pages/reviews/the-edison-wave-review/page'));
const NeuroThriveReview = lazy(() => import('../pages/reviews/neuro-thrive-review/page'));

import BalmorexReview from '../pages/reviews/balmorex-review/page';

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/blog',
    element: <Blog />,
  },
  {
    path: '/blog/how-to-take-mitolyn',
    element: <HowToTakeMitolyn />,
  },
  {
    path: '/blog/mitolyn-price-breakdown',
    element: <MitolynPriceBreakdown />,
  },
  {
    path: '/blog/where-to-buy-mitolyn',
    element: <WhereToBuyMitolynPost />,
  },
  {
    path: '/blog/mitolyn-side-effects',
    element: <MitolynSideEffectsPost />,
  },
  {
    path: '/blog/mitolyn-ingredients-review',
    element: <MitolynIngredientsReviewPost />,
  },
  {
    path: '/blog/mitolyn-scam-or-legit',
    element: <MitolynScamOrLegitPost />,
  },
  {
    path: '/blog/does-mitolyn-really-work',
    element: <DoesMitolynReallyWork />,
  },
  {
    path: '/blog/why-slow-metabolism-makes-weight-loss-hard',
    element: <WhySlowMetabolismMakesWeightLossHard />,
  },
  {
    path: '/blog/common-keto-baking-mistakes',
    element: <CommonKetoBakingMistakes />,
  },
  {
    path: '/blog/why-keto-desserts-are-popular',
    element: <WhyKetoDessertArePopular />,
  },
  {
    path: '/blog/essential-tools-for-keto-baking',
    element: <EssentialToolsForKetoBaking />,
  },
  {
    path: '/blog/keto-baking-beginners-guide',
    element: <KetoBakingBeginnersGuide />,
  },
  {
    path: '/blog/keto-baking-for-beginners',
    element: <KetoBakingForBeginners />,
  },
  {
    path: '/blog/:slug',
    element: <BlogPost />,
  },
  {
    path: '/about',
    element: <AboutPage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/disclaimer',
    element: <DisclaimerPage />,
  },
  {
    path: '/affiliate-disclosure',
    element: <AffiliateDisclosurePage />,
  },
  {
    path: '/category/:slug',
    element: <CategoryPage />,
  },
  {
    path: '/article/:slug',
    element: <ArticlePage />,
  },
  {
    path: '/reviews/pianoforall-review',
    element: <PianoforallReview />,
  },
  {
    path: '/reviews/liberty-token-review',
    element: <LibertyTokenReview />,
  },
  {
    path: '/reviews/laserless-tattoo-removal-review',
    element: <LaserlessTattooRemovalReview />,
  },
  {
    path: '/reviews/the-lay-formula-review',
    element: <TheLayFormulaReview />,
  },
  {
    path: '/reviews/7-bets-4-free-review',
    element: <SevenBets4FreeReview />,
  },
  {
    path: '/reviews/daily-football-winners-review',
    element: <DailyFootballWinnersReview />,
  },
  {
    path: '/reviews/zcode-system-review',
    element: <ZcodeSystemReview />,
  },
  {
    path: '/reviews/profit-maximiser-review',
    element: <ProfitMaximiserReview />,
  },
  {
    path: '/reviews/betting-gods-review',
    element: <BettingGodsReview />,
  },
  {
    path: '/reviews/football-booster-tips-review',
    element: <FootballBoosterTipsReview />,
  },
  {
    path: '/reviews/breakthrough-bets-review',
    element: <BreakthroughBetsReview />,
  },
  {
    path: '/reviews/bank-on-football-review',
    element: <BankOnFootballReview />,
  },
  {
    path: '/reviews/lottery-maximizer-review',
    element: <LotteryMaximizerReview />,
  },
  {
    path: '/reviews/exterminator-system-review',
    element: <ExterminatorSystemReview />,
  },
  {
    path: '/reviews/betting-scientist-review',
    element: <BettingScientistReview />,
  },
  {
    path: '/reviews/goal-predictor-review',
    element: <GoalPredictorReview />,
  },
  {
    path: '/reviews/value-multiplier-review',
    element: <ValueMultiplierReview />,
  },
  {
    path: '/reviews/vip-indicators-review',
    element: <VipIndicatorsReview />,
  },
  {
    path: '/reviews/woodprofits-review',
    element: <WoodProfitsReview />,
  },
  {
    path: '/reviews/leskohelp-review',
    element: <LeskoHelpReview />,
  },
  {
    path: '/reviews/spy-sniper-trading-system-review',
    element: <SpySniperTradingSystemReview />,
  },
  {
    path: '/reviews/business-credit-builder-review',
    element: <BusinessCreditBuilderReview />,
  },
  {
    path: '/reviews/options-pop-review',
    element: <OptionsPopReview />,
  },
  {
    path: '/reviews/bake-a-dog-a-bone-review',
    element: <BakeADogABoneReview />,
  },
  {
    path: '/reviews/microbanking-method-review',
    element: <MicroBankingMethodReview />,
  },
  {
    path: '/reviews/fat-burning-kitchen-review',
    element: <FatBurningKitchenReview />,
  },
  {
    path: '/reviews/complete-plant-based-cookbook-review',
    element: <CompletePlantBasedCookbookReview />,
  },
  {
    path: '/reviews/keto-bakery-bundle-review',
    element: <KetoBakeryBundleReview />,
  },
  {
    path: '/reviews/200-delicious-smoothie-recipes-review',
    element: <SmoothieRecipesReview />,
  },
  {
    path: '/reviews/500-mouthwatering-dessert-recipes-review',
    element: <DessertRecipesReview />,
  },
  {
    path: '/reviews/500-keto-diet-recipes-review',
    element: <KetoRecipesReview />,
  },
  {
    path: '/reviews/250-vegan-recipes-review',
    element: <VeganRecipesReview />,
  },
  {
    path: '/reviews/write-app-reviews-review',
    element: <WriteAppReviewsReview />,
  },
  {
    path: '/reviews/profit-vault-review',
    element: <ProfitVaultReview />,
  },
  {
    path: '/reviews/ai-marketers-club-review',
    element: <AIMarketersClubReview />,
  },
  {
    path: '/reviews/evolution-review',
    element: <EvolutionReview />,
  },
  {
    path: '/reviews/10-minute-cash-system-review',
    element: <TenMinuteCashSystemReview />,
  },
  {
    path: '/reviews/empire-review',
    element: <EmpireReview />,
  },
  {
    path: '/reviews/paid-online-writing-jobs-review',
    element: <PaidOnlineWritingJobsReview />,
  },
  {
    path: '/reviews/salehoo-review',
    element: <SaleHooReview />,
  },
  {
    path: '/reviews/profit-fundamentals-review',
    element: <ProfitFundamentalsReview />,
  },
  {
    path: '/reviews/click-earners-review',
    element: <ClickEarnersReview />,
  },
  {
    path: '/reviews/recession-profit-secrets-review',
    element: <RecessionProfitSecretsReview />,
  },
  {
    path: '/reviews/idplr-review',
    element: <IDPLRReview />,
  },
  {
    path: '/reviews/go-digital-income-review',
    element: <GoDigitalIncomeReview />,
  },
  {
    path: '/reviews/ez-cover-maker-review',
    element: <EZCoverMakerReview />,
  },
  {
    path: '/reviews/commissionable-review',
    element: <CommissionableReview />,
  },
  {
    path: '/reviews/get-paid-social-media-review',
    element: <GetPaidSocialMediaReview />,
  },
  {
    path: '/reviews/secrets-of-the-big-dogs-review',
    element: <SecretsOfTheBigDogsReview />,
  },
  {
    path: '/reviews/live-chat-jobs-review',
    element: <LiveChatJobsReview />,
  },
  {
    path: '/reviews/2026-social-media-secrets-review',
    element: <SocialMediaSecretsReview />,
  },
  {
    path: '/reviews/instadoodle-review',
    element: <InstaDoodleReview />,
  },
  {
    path: '/reviews/speechelo-review',
    element: <SpeecheloReview />,
  },
  {
    path: '/reviews/videly-review',
    element: <VidelyReview />,
  },
  {
    path: '/reviews/medical-courier-jobs-review',
    element: <MedicalCourierJobsReview />,
  },
  {
    path: '/reviews/energy-revolution-system-review',
    element: <EnergyRevolutionSystemReview />,
  },
  {
    path: '/reviews/septifix-review',
    element: <SeptifixReview />,
  },
  {
    path: '/reviews/ultimate-off-grid-generator-review',
    element: <UltimateOffGridGeneratorReview />,
  },
  {
    path: '/reviews/ez-battery-reconditioning-review',
    element: <EZBatteryReconditioningReview />,
  },
  {
    path: '/reviews/home-power-shield-review',
    element: <HomePowerShieldReview />,
  },
  {
    path: '/reviews/kinetic-power-system-review',
    element: <KineticPowerSystemReview />,
  },
  {
    path: '/reviews/solar-innovator-review',
    element: <SolarInnovatorReview />,
  },
  {
    path: '/reviews/hendershot-power-generator-review',
    element: <HendershotPowerGeneratorReview />,
  },
  {
    path: '/reviews/easy-diy-power-plan-review',
    element: <EasyDIYPowerPlanReview />,
  },
  {
    path: '/reviews/quick-power-system-review',
    element: <QuickPowerSystemReview />,
  },
  {
    path: '/reviews/infinite-energy-system-review',
    element: <InfiniteEnergySystemReview />,
  },
  {
    path: '/reviews/the-ultimate-energizer-review',
    element: <TheUltimateEnergizerReview />,
  },
  {
    path: '/reviews/home-grid-freedom-review',
    element: <HomeGridFreedomReview />,
  },
  {
    path: '/reviews/tyranny-liberator-review',
    element: <TyrannyLiberatorReview />,
  },
  {
    path: '/reviews/energy-liberation-army-review',
    element: <EnergyLiberationArmyReview />,
  },
  {
    path: '/reviews/tornado-generator-review',
    element: <TornadoGeneratorReview />,
  },
  {
    path: '/reviews/stirling-free-power-review',
    element: <StirlingFreePowerReview />,
  },
  {
    path: '/reviews/ground-power-generator-review',
    element: <GroundPowerGeneratorReview />,
  },
  {
    path: '/reviews/diy-dish-system-review',
    element: <DIYDishSystemReview />,
  },
  {
    path: '/reviews/the-freedom-box-review',
    element: <TheFreedomBoxReview />,
  },
  {
    path: '/reviews/spartamax-review',
    element: <SpartamaxReview />,
  },
  {
    path: '/reviews/the-blood-pressure-program-review',
    element: <TheBloodPressureProgramReview />,
  },
  {
    path: '/reviews/mitolyn-review',
    element: <MitolynReview />,
  },
  {
    path: '/reviews/prostavive-review',
    element: <ProstaViveReview />,
  },
  {
    path: '/reviews/citrusburn-review',
    element: <CitrusBurnReview />,
  },
  {
    path: '/reviews/audifort-review',
    element: <AudiofortReviewPage />,
  },
  {
    path: '/reviews/prodentim-review',
    element: <ProdentimReviewPage />,
  },
  {
    path: '/reviews/neuro-energizer-review',
    element: <NeuroEnergizerReviewPage />,
  },
  {
    path: '/reviews/gluco6-review',
    element: <Gluco6ReviewPage />,
  },
  {
    path: '/reviews/provadent-review',
    element: <ProvaDentReviewPage />,
  },
  {
    path: '/reviews/the-memory-wave-review',
    element: <TheMemoryWaveReviewPage />,
  },
  {
    path: '/reviews/leanbiome-review',
    element: <LeanBiomeReview />,
  },
  {
    path: '/reviews/the-smoothie-diet-review',
    element: <TheSmoothieDietReview />,
  },
  {
    path: '/reviews/sleeplean-review',
    element: <SleepLeanReview />,
  },
  {
    path: '/reviews/java-burn-review',
    element: <JavaBurnReview />,
  },
  {
    path: '/reviews/igenics-review',
    element: <IGenicsReview />,
  },
  {
    path: '/reviews/sugar-defender-review',
    element: <SugarDefenderReview />,
  },
  {
    path: '/reviews/neuro-serge-review',
    element: <NeuroSergeReview />,
  },
  {
    path: '/reviews/cardio-slim-tea-review',
    element: <CardioSlimTeaReview />,
  },
  {
    path: '/reviews/finessa-review',
    element: <FinessaReview />,
  },
  {
    path: '/reviews/pineal-guardian-x-review',
    element: <PinealGuardianXReview />,
  },
  {
    path: '/reviews/liv-pure-review',
    element: <LivPureReview />,
  },
  {
    path: '/reviews/ikaria-juice-review',
    element: <IkariaJuiceReview />,
  },
  {
    path: '/reviews/neuro-balance-therapy-review',
    element: <NeuroBalanceTherapyReview />,
  },
  {
    path: '/reviews/arcticblast-review',
    element: <ArcticBlastReview />,
  },
  {
    path: '/reviews/hepatoburn-review',
    element: <HepatoBurnReview />,
  },
  {
    path: '/reviews/prosta-peak-review',
    element: <ProstaPeakReview />,
  },
  {
    path: '/reviews/gluco-extend-review',
    element: <GlucoExtendReview />,
  },
  {
    path: '/reviews/bellyflush-review',
    element: <BellyFlushReview />,
  },
  {
    path: '/reviews/bone-density-solution-review',
    element: <BoneDensitySolutionReview />,
  },
  {
    path: '/reviews/protoflow-review',
    element: <ProtoflowReview />,
  },
  {
    path: '/reviews/biodentex-review',
    element: <BioDentexReview />,
  },
  {
    path: '/reviews/zencortex-review',
    element: <ZenCortexReview />,
  },
  {
    path: '/reviews/neuroprime-review',
    element: <NeuroPrimeReview />,
  },
  {
    path: '/reviews/nagano-tonic-review',
    element: <NaganoTonicReview />,
  },
  {
    path: '/reviews/jointvive-review',
    element: <JointViveReview />,
  },
  {
    path: '/reviews/glucoberry-review',
    element: <GlucoBerryReview />,
  },
  {
    path: '/reviews/balmorex-review',
    element: <BalmorexReview />,
  },
  {
    path: '/reviews/titanflow-review',
    element: <TitanFlowReview />,
  },
  {
    path: '/reviews/metabo-drops-review',
    element: <MetaboDropsReview />,
  },
  {
    path: '/reviews/the-parkinsons-protocol-review',
    element: <TheParkinsonsProtocolReview />,
  },
  {
    path: '/reviews/slim-crystal-review',
    element: <SlimCrystalReview />,
  },
  {
    path: '/reviews/14-day-rapid-soup-diet-review',
    element: <FourteenDayRapidSoupDietReview />,
  },
  {
    path: '/reviews/sugarmute-review',
    element: <SugarMuteReview />,
  },
  {
    path: '/reviews/thyrafemme-balance-review',
    element: <ThyrafemmeBalanceReview />,
  },
  {
    path: '/reviews/gut-vita-review',
    element: <GutVitaReview />,
  },
  {
    path: '/reviews/venoplus-8-review',
    element: <VenoPlus8Review />,
  },
  {
    path: '/reviews/the-edison-wave-review',
    element: <TheEdisonWaveReview />,
  },
  {
    path: '/reviews/neuro-thrive-review',
    element: <NeuroThriveReview />,
  },
  {
    path: '/reviews/neuro-thrive-review',
    element: <NeuroThriveReview />,
  },
  {
    path: '/reviews/neuro-serge-review',
    element: <NeuroSergeReview />,
  },
  {
    path: '/reviews/cardio-slim-tea-review',
    element: <CardioSlimTeaReview />,
  },
  {
    path: '/reviews/finessa-review',
    element: <FinessaReview />,
  },
  {
    path: '/reviews/pineal-guardian-x-review',
    element: <PinealGuardianXReview />,
  },
  {
    path: '/reviews/liv-pure-review',
    element: <LivPureReview />,
  },
  {
    path: '/reviews/ikaria-juice-review',
    element: <IkariaJuiceReview />,
  },
  {
    path: '/reviews/neuro-balance-therapy-review',
    element: <NeuroBalanceTherapyReview />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;